<template>
    <kiosk-group-details :teamId="props.teamId" :groupId="props.groupId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) flex flex-col">
                <page-callout section="kiosk-group-billing-details" class="mb-4" />

                <page-header>Billing Details</page-header>

                <group-billing-email
                    class="mb-4"
                    :teamId="group.team_id"
                    :groupId="group.group_id"
                    :billingEmail="group.billing_email"
                    :isEditable="isEditable"
                    :allowUnset="true"
                    :unsetting="unsettingEmail"
                    @unset="confirmUnsetEmail"
                />

                <group-billing-info
                    class="mb-4"
                    :teamId="group.team_id"
                    :groupId="group.group_id"
                    :billingInformation="{
                        billing_company_legal_name: group.billing_company_legal_name,
                        billing_address: group.billing_address,
                        billing_address_line_2: group.billing_address_line_2,
                        billing_city: group.billing_city,
                        billing_state: group.billing_state,
                        billing_zip: group.billing_zip,
                        billing_country: group.billing_country,
                        billing_po_number: group.billing_po_number,
                    }"
                    :isEditable="isEditable"
                    :allowUnset="true"
                    :unsetting="unsettingInformation"
                    @unset="confirmUnsetInformation"
                />

                <group-stripe-customer v-if="stripeKey && isEditable" class="mb-4" :stripeId="group.stripe_id" />
            </div>
        </div>
    </kiosk-group-details>
</template>

<script setup>
import { VxSwal } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import KioskGroupDetails from '~/components/kiosk/kiosk-groups/details/KioskGroupDetails.vue';
import { useGroupDetails } from '~/components/kiosk/kiosk-groups/composables/useGroupDetails';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import GroupBillingEmail from '~/components/settings/billing/GroupBillingEmail.vue';
import GroupBillingInfo from '~/components/settings/billing/GroupBillingInfo.vue';
import GroupStripeCustomer from '~/components/settings/billing/GroupStripeCustomer.vue';
import { useToasted } from '~/composables/useToasted';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { adminGroupsClient } from '~/services';

const { group, refreshGroup } = useGroupDetails();

const props = defineProps({
    teamId: { type: Number, required: true },
    groupId: { type: String, required: true },
});

const stripeKey = Spark.stripeKey;
const isEditable = computed(() => group.value.is_billable);

const toasted = useToasted();
const { catchError } = useFormRequestHandler();
const unsettingInformation = ref(false);
const unsettingEmail = ref(false);

const confirmUnsetInformation = () => {
    VxSwal.confirmation(
        'Unset billing information',
        'This permanently clears the billing address, company name, and PO number for this group. This action cannot be undone.',
        'danger',
        () => unsetInformation()
    );
};

const unsetInformation = async () => {
    if (unsettingInformation.value) {
        return;
    }

    unsettingInformation.value = true;
    try {
        await adminGroupsClient.unsetBillingInformation(props.teamId, props.groupId);
        await refreshGroup(props.teamId, props.groupId);
        toasted.success('Billing information cleared.');
    } catch (e) {
        catchError(e);
    } finally {
        unsettingInformation.value = false;
    }
};

const confirmUnsetEmail = () => {
    VxSwal.confirmation(
        'Unset billing email',
        'This permanently clears the billing email for this group. This action cannot be undone.',
        'danger',
        () => unsetEmail()
    );
};

const unsetEmail = async () => {
    if (unsettingEmail.value) {
        return;
    }

    unsettingEmail.value = true;
    try {
        await adminGroupsClient.unsetBillingEmail(props.teamId, props.groupId);
        await refreshGroup(props.teamId, props.groupId);
        toasted.success('Billing email cleared.');
    } catch (e) {
        catchError(e);
    } finally {
        unsettingEmail.value = false;
    }
};
</script>
